/**
 * Écran « À arbitrer » : les écritures Zoho classées par une règle ou par Jev,
 * celles dont la proposition reste à vérifier et celles qu'aucune règle ne
 * couvre. L'administration arbitre ; la lecture consulte.
 */

import { dateFr, entier, eurosCentimes } from '@scc/core';
import { ErreurApi, type Identite } from '../../api.js';
import { enteteSection, noteLecture } from '../../composants.js';
import { h, vider } from '../../dom.js';
import {
  arbitrer,
  attendreFinDePasse,
  creerRegle,
  ecrituresClassees,
  listeSousPostes,
  phraseBilan,
  relancer,
  resume,
  surFinDePasse,
  validerLot,
  type ChampRegle,
  type EcritureClassee,
  type EtatPasse,
  type SensEcriture,
  type SousPoste,
  type Suggestion,
} from './api.js';
import { libelleCompte } from './comptes.js';
import { champRechercheSousPoste } from './recherche-sous-poste.js';
import { avecVerrou } from './verrou.js';

interface Onglet {
  readonly cle: string;
  readonly libelle: string;
  readonly statut?: string;
  readonly source?: string;
  /** Statut que « Valider les propositions » vise sur cet onglet ; absent : action indisponible. */
  readonly lot?: 'a_verifier' | 'auto';
}

const ONGLETS: readonly Onglet[] = [
  { cle: 'a_verifier', libelle: 'À vérifier', statut: 'a_verifier', lot: 'a_verifier' },
  { cle: 'non_classe', libelle: 'Non classées', statut: 'non_classe' },
  { cle: 'compte', libelle: 'Par compte', source: 'compte' },
  { cle: 'jev', libelle: 'Par Jev', source: 'jev', lot: 'auto' },
  { cle: 'regle', libelle: 'Par règle', source: 'regle' },
  { cle: 'toutes', libelle: 'Toutes' },
];

const CHAMPS_SUGGESTION: Readonly<Record<string, string>> = {
  tiers: 'tiers contient',
  libelle: 'libellé contient',
  compte_libelle: 'compte égal à',
};

const SOURCES: Readonly<Record<string, string>> = {
  compte: 'Compte Zoho',
  regle: 'Règle',
  jev: 'Jev',
  arbitrage: 'Arbitrage',
};

const pct = (x: number): string => `${String(Math.round(x * 100))} %`;

export function vueArbitrage(identite: Identite, actualiser?: () => void): HTMLElement {
  const admin = identite.role === 'administration';
  const kpis = h('div', { classe: 'kpi-grid' });
  const etatPasse = h('p', { classe: 'note-lecture', role: 'status' });
  const retour = h('div', { role: 'status' });
  const bandeau = h('div', {});
  const lot = h('div', {});
  const corps = h(
    'div',
    { role: 'tabpanel', id: 'arbitrage-panneau', 'aria-labelledby': `arbitrage-onglet-${ONGLETS[0]?.cle ?? 'toutes'}` },
    h('p', { classe: 'note-lecture' }, 'Chargement…'),
  );
  const boutonActualiser = h(
    'button',
    {
      classe: 'btn-action',
      type: 'button',
      hidden: true,
      surClick: () => {
        actualiser?.();
      },
    },
    'Actualiser les montants du tableau de bord',
  );
  let onglet: Onglet = ONGLETS[0] ?? { cle: 'toutes', libelle: 'Toutes' };
  let sousPostes: SousPoste[] = [];
  let generation = 0;
  /** Une passe tourne, ou une relance vient d'être demandée : « Relancer » reste désactivé. */
  let passeEnCours = false;
  let relanceDemandee = false;
  const boutonRelancer = h(
    'button',
    {
      classe: 'btn-action',
      type: 'button',
      surClick: () => {
        relancerClassement();
      },
    },
    'Relancer le classement',
  );
  const majRelancer = (): void => {
    boutonRelancer.disabled = passeEnCours || relanceDemandee;
  };

  const signaler = (texte: string, ok: boolean): void => {
    vider(retour);
    retour.appendChild(h('p', { classe: `retour-editeur ${ok ? 'ok' : 'ko'}`, role: ok ? null : 'alert' }, texte));
  };
  const echec = (e: unknown): void => {
    signaler(e instanceof ErreurApi ? e.message : 'Opération refusée.', false);
  };
  const aChange = (): void => {
    if (actualiser) boutonActualiser.hidden = false;
  };

  const afficherPasse = (p: EtatPasse): void => {
    etatPasse.textContent = p.enCours ? 'Classement en cours…' : (phraseBilan(p) ?? '');
    passeEnCours = p.enCours;
    majRelancer();
  };

  /** Suit la passe jusqu'à son terme ; le rechargement des données revient à l'abonnement. */
  const suivrePasse = async (messageFin?: string): Promise<void> => {
    try {
      const r = await attendreFinDePasse();
      afficherPasse(r.passe);
      if (messageFin) signaler(messageFin, true);
    } catch (e) {
      echec(e);
    }
  };

  const chargerResume = async (): Promise<void> => {
    const r = await resume();
    vider(kpis);
    const carte = (v: number, l: string, ton: string): HTMLElement =>
      h('div', { classe: `kpi ${ton}` }, h('div', { classe: 'kpi-value' }, entier(v)), h('div', { classe: 'kpi-label' }, l));
    kpis.append(
      carte(r.auto + r.arbitres, 'classées', 'progress'),
      carte(r.aVerifier, 'à vérifier', 'warning'),
      carte(r.nonClasses, 'non classées', 'neutral'),
      carte(r.parSource.compte, 'classées par leur compte Zoho', 'progress'),
    );
    afficherPasse(r.passe);
    // Une passe lancée ailleurs (démarrage, autre écran) se suit aussi ici.
    if (r.passe.enCours) void suivrePasse();
  };

  const proposer = (s: Suggestion, sp: SousPoste, sens: SensEcriture): void => {
    vider(bandeau);
    const champ = h(
      'select',
      { 'aria-label': 'Critère de la règle' },
      ...Object.entries(CHAMPS_SUGGESTION).map(([v, l]) => h('option', { value: v }, l)),
    );
    champ.value = Object.hasOwn(CHAMPS_SUGGESTION, s.champ) ? s.champ : 'libelle';
    const valeur = h('input', { type: 'text', maxlength: 200, 'aria-label': 'Valeur du critère' });
    valeur.value = s.valeur;
    bandeau.appendChild(
      h(
        'div',
        { classe: 'card bandeau-suggestion' },
        h('span', {}, 'Toujours classer '),
        champ,
        valeur,
        h('span', {}, ` en « ${libelleCompte(sp)} » ?`),
        h(
          'button',
          {
            classe: 'btn-action',
            type: 'button',
            surClick: (ev) => {
              const bouton = ev.currentTarget as HTMLButtonElement;
              void (async () => {
                try {
                  const c = champ.value as ChampRegle;
                  await avecVerrou([bouton, champ, valeur], async () => {
                    await creerRegle({
                      champ: c,
                      operateur: c === 'compte_libelle' ? 'egal' : 'contient',
                      valeur: valeur.value,
                      sens,
                      sousPoste: sp.id,
                      origine: 'arbitrage',
                    });
                  });
                  vider(bandeau);
                  signaler('Règle créée ; reclassement des écritures en cours…', true);
                  aChange();
                  afficherPasse({ enCours: true, jev: false, debut: null, fin: null, dernierBilan: null, erreur: false });
                  await suivrePasse('Règle créée et appliquée.');
                } catch (e) {
                  echec(e);
                }
              })();
            },
          },
          'Créer la règle',
        ),
        h(
          'button',
          {
            type: 'button',
            surClick: () => {
              vider(bandeau);
            },
          },
          'Non merci',
        ),
      ),
    );
  };

  /** Sous-postes actifs qui acceptent une écriture du sens donné. */
  const sousPostesAdmis = (sens: SensEcriture): SousPoste[] =>
    sousPostes.filter((s) => !s.archive && (s.sens === sens || s.sens === 'tous'));

  const libelleAlternative = (id: string): string => {
    const sp = sousPostes.find((s) => s.id === id);
    return sp ? libelleCompte(sp) : id;
  };

  const ligne = (e: EcritureClassee): HTMLElement => {
    const choix = sousPostesAdmis(e.sens);
    // Exercice habituel de l'écriture ; l'arbitrage ne le surcharge que s'il est changé.
    const habituel = e.exerciceAffectation ?? Number(e.date.slice(0, 4));
    const initial = e.exercice ?? habituel;
    const exercice = h('input', {
      type: 'number',
      min: 2000,
      max: 2100,
      step: 1,
      classe: 'champ-exercice',
      'aria-label': 'Exercice de rattachement',
    });
    exercice.value = String(initial);
    const tr = h('tr', {});

    const choisir = (id: string): void => {
      const saisi = Number(exercice.value);
      if (!Number.isInteger(saisi) || saisi < 2000 || saisi > 2100) {
        signaler('Exercice invalide : indiquez une année entre 2000 et 2100.', false);
        recherche.reinitialiser();
        return;
      }
      // Inchangé : omis (la surcharge existante est conservée) ; revenu à
      // l'exercice habituel : `null` la retire ; sinon la nouvelle valeur.
      const surcharge = saisi === initial ? undefined : saisi === habituel ? null : saisi;
      // L'onglet affiché au moment du clic : s'il change pendant la requête,
      // la ligne n'est plus à retirer et la liste affichée est à recharger.
      const ongletInitial = onglet;
      const controles = [...tr.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button, input')];
      void (async () => {
        try {
          const r = await avecVerrou(controles, () => arbitrer(e.transactionId, id, surcharge));
          aChange();
          const sp = sousPostes.find((s) => s.id === id);
          if (r.suggestion && sp) {
            proposer(r.suggestion, sp, r.sens);
            signaler('Arbitrage enregistré — une règle est proposée ci-dessus.', true);
          } else {
            vider(bandeau);
            signaler('Arbitrage enregistré.', true);
          }
          if (onglet !== ongletInitial) {
            await charger();
          } else if (onglet.statut === 'a_verifier' || onglet.statut === 'non_classe') {
            const voisine = tr.nextElementSibling ?? tr.previousElementSibling;
            tr.remove();
            await chargerResume();
            if (!verifierVide()) voisine?.querySelector<HTMLElement>('input[role="combobox"]')?.focus();
          } else {
            const y = window.scrollY;
            await charger();
            window.scrollTo(0, y);
          }
        } catch (err) {
          recherche.reinitialiser();
          echec(err);
        }
      })();
    };

    // Le champ de recherche remplace la liste déroulante : plus d'une centaine de comptes par sens.
    const recherche = champRechercheSousPoste({
      choix,
      surChoix: choisir,
      libelle: `Classer « ${e.tiers || e.libelle || e.compteLibelle} » dans`,
    });

    const alternatives = e.alternatives
      .filter((a) => a.sousPoste !== e.sousPoste && choix.some((s) => s.id === a.sousPoste))
      .slice(0, 3);

    const cellules = [
      h('td', {}, dateFr(e.date)),
      h(
        'td',
        {},
        h('strong', {}, e.tiers || e.libelle || e.compteLibelle),
        e.tiers && e.libelle ? h('div', { classe: 'note-lecture' }, e.libelle) : null,
      ),
      h('td', { classe: 'num' }, `${e.sens === 'recette' ? '+' : '−'} ${eurosCentimes(e.montant)}`),
      h(
        'td',
        {},
        e.sousPosteLibelle === null ? '—' : libelleCompte({ code: e.sousPosteCode ?? '', libelle: e.sousPosteLibelle }),
        e.confiance !== null && e.source === 'jev' ? h('span', { classe: 'note-lecture' }, ` · ${pct(e.confiance)}`) : null,
        SOURCES[e.source] ? h('div', { classe: 'note-lecture' }, SOURCES[e.source]) : null,
      ),
      admin
        ? h(
            'td',
            {},
            h(
              'div',
              { classe: 'actions-arbitrage' },
              ...alternatives.map((a) =>
                h(
                  'button',
                  {
                    type: 'button',
                    classe: 'btn-action',
                    surClick: () => {
                      choisir(a.sousPoste);
                    },
                  },
                  `${libelleAlternative(a.sousPoste)} · ${pct(a.probabilite)}`,
                ),
              ),
              recherche.element,
              exercice,
            ),
          )
        : null,
    ];
    for (const c of cellules) if (c) tr.appendChild(c);
    return tr;
  };

  /**
   * Remplace le tableau par un message quand la dernière ligne a disparu, et y
   * place le focus : le contrôle qui l'avait vient de quitter le document.
   * Renvoie vrai si le tableau était vide.
   */
  const verifierVide = (): boolean => {
    if (corps.querySelector('tbody > tr')) return false;
    vider(corps);
    const avis = noteLecture('Aucune écriture dans cet onglet.');
    avis.tabIndex = -1;
    corps.appendChild(avis);
    avis.focus();
    return true;
  };

  const charger = async (): Promise<void> => {
    const moi = ++generation;
    try {
      const [{ ecritures }, sp] = await Promise.all([
        ecrituresClassees(onglet.statut, onglet.source),
        listeSousPostes(),
        chargerResume(),
      ]);
      if (moi !== generation) return;
      sousPostes = sp.sousPostes;
      vider(corps);
      if (ecritures.length === 0) {
        corps.appendChild(noteLecture('Aucune écriture dans cet onglet.'));
        return;
      }
      corps.appendChild(
        h(
          'table',
          { classe: 'comp-table tableau-arbitrage' },
          h('caption', { classe: 'invisible' }, `Écritures — ${onglet.libelle}`),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { scope: 'col' }, 'Date'),
              h('th', { scope: 'col' }, 'Écriture'),
              h('th', { scope: 'col', classe: 'num' }, 'Montant'),
              h('th', { scope: 'col' }, 'Classement'),
              admin ? h('th', { scope: 'col' }, 'Arbitrer') : null,
            ),
          ),
          h('tbody', {}, ...ecritures.map(ligne)),
        ),
      );
    } catch (e) {
      if (moi !== generation) return;
      vider(corps);
      corps.appendChild(noteLecture('Les écritures n’ont pas pu être chargées.'));
      echec(e);
    }
  };

  const seuil = h('input', { type: 'number', min: 0, max: 100, step: 1, classe: 'champ-seuil' });
  seuil.value = '90';

  /** L'action groupée ne vise qu'un statut : elle n'existe que sur les onglets qui en ont un. */
  const dessinerLot = (): void => {
    vider(lot);
    if (!admin || !onglet.lot) return;
    const statut = onglet.lot;
    lot.appendChild(
      h(
        'div',
        { classe: 'barre-actions' },
        h('label', {}, 'Valider les propositions de Jev dont la confiance est ≥ ', seuil, ' %'),
        h(
          'button',
          {
            classe: 'btn-action',
            type: 'button',
            surClick: (ev) => {
              const bouton = ev.currentTarget as HTMLButtonElement;
              const s = Number(seuil.value);
              if (!Number.isFinite(s) || s < 0 || s > 100) {
                signaler('Seuil invalide : indiquez un pourcentage entre 0 et 100.', false);
                return;
              }
              void (async () => {
                try {
                  const r = await avecVerrou([bouton, seuil], () => validerLot(s / 100, statut));
                  signaler(`${String(r.valides)} proposition(s) validée(s).`, true);
                  if (r.valides > 0) aChange();
                  await charger();
                } catch (e) {
                  echec(e);
                }
              })();
            },
          },
          'Valider',
        ),
      ),
    );
  };

  const boutonsOnglets = h(
    'div',
    { classe: 'onglets', role: 'tablist', 'aria-label': 'Filtrer les écritures' },
    ...ONGLETS.map((o) => {
      const b = h(
        'button',
        {
          classe: 'btn-action',
          type: 'button',
          role: 'tab',
          id: `arbitrage-onglet-${o.cle}`,
          'aria-controls': 'arbitrage-panneau',
          'aria-selected': o === onglet ? 'true' : 'false',
          surClick: () => {
            onglet = o;
            corps.setAttribute('aria-labelledby', `arbitrage-onglet-${o.cle}`);
            for (const x of boutonsOnglets.querySelectorAll('button')) {
              x.setAttribute('aria-selected', x === b ? 'true' : 'false');
            }
            vider(bandeau);
            dessinerLot();
            void charger();
          },
        },
        o.libelle,
      );
      return b;
    }),
  );

  /** Lance une passe avec Jev puis la suit ; « Relancer » reste désactivé tant qu'une passe tourne. */
  const relancerClassement = (): void => {
    if (passeEnCours || relanceDemandee) return;
    relanceDemandee = true;
    majRelancer();
    void (async () => {
      try {
        try {
          await relancer();
        } catch (e) {
          // Une passe avec Jev tourne déjà : on la suit au lieu d'en lancer une autre.
          if (!(e instanceof ErreurApi && e.statut === 409)) {
            echec(e);
            return;
          }
          signaler(e.message, true);
        }
        afficherPasse({ enCours: true, jev: true, debut: null, fin: null, dernierBilan: null, erreur: false });
        await suivrePasse('Classement terminé.');
        aChange();
      } finally {
        relanceDemandee = false;
        majRelancer();
      }
    })();
  };

  const actions = admin ? h('div', { classe: 'barre-actions' }, boutonRelancer, boutonActualiser) : null;

  const racine = h(
    'div',
    { classe: 'page', id: 'page-arbitrage', role: 'region', 'aria-label': 'À arbitrer' },
    h(
      'section',
      {},
      enteteSection(1, 'À arbitrer', 'Écritures Zoho classées par règle ou par Jev'),
      noteLecture(
        'Jev propose un classement pour les écritures qu’aucune règle ne couvre. Sous le seuil de confiance, ' +
          'la proposition attend votre arbitrage. Chaque arbitrage peut devenir une règle.',
      ),
      kpis,
      etatPasse,
      actions,
      retour,
      bandeau,
      boutonsOnglets,
      lot,
      corps,
    ),
  );
  // Les autres écrans (règles, sous-postes) peuvent lancer un reclassement.
  // Une vue retirée du document (changement de thème) se désabonne au premier appel.
  const desabonner = surFinDePasse(() => {
    if (!racine.isConnected) {
      desabonner();
      return;
    }
    void charger();
  });
  return racine;
}
